import React, { useContext, useEffect, useMemo } from 'react'
import { ChakraProvider } from '@chakra-ui/react'
import { DEFAULT_PLATFORM_THEME } from '@reward-platform/platform-config'
import { logger } from '~/utils/datadog'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { getChakraThemeFromKey } from './chakraUtils'
import { ThemeContextValue, ThemeProviderProps } from './ThemeContext.types'

import '@channel/shared-header-footer/style.css'

const ThemeContext = React.createContext<ThemeContextValue>({
  themeId: DEFAULT_PLATFORM_THEME,
  isAviosTheme: true,
  isWhitelabel: false,
})

const loadTokensAndFonts = async (altoThemeId: ThemeContextValue['themeId']) => {
  switch (altoThemeId) {
    case 'british_airways':
      await import('@alto-avios/alto-tokens/web-ssr/british-airways/light.css')
      await import('@alto-avios/alto-tokens/web-ssr/british-airways/fonts.css')
      break
    case 'aer_lingus':
      await import('@alto-avios/alto-tokens/web-ssr/aer-lingus/light.css')
      await import('@alto-avios/alto-tokens/web-ssr/aer-lingus/fonts.css')
      break
    case 'iberia':
      await import('@alto-avios/alto-tokens/web-ssr/iberia/light.css')
      await import('@alto-avios/alto-tokens/web-ssr/iberia/fonts.css')
      break
    case 'avios':
    default:
      await import('@alto-avios/alto-tokens/web-ssr/avios/light.css')
      await import('@alto-avios/alto-tokens/web-ssr/avios/fonts.css')
      break
  }
}

export const ThemeProvider = ({
  children,
  themeId,
  shouldDynamicallyLoadCSS = true,
  nonce,
}: React.PropsWithChildren<ThemeProviderProps>) => {
  const styleCache = useMemo(
    () =>
      createCache({
        key: 'chakra-ui',
        nonce,
      }),
    [nonce]
  )

  useEffect(() => {
    if (shouldDynamicallyLoadCSS) {
      loadTokensAndFonts(themeId).catch((err) =>
        logger.error(`Error loading tokens/fonts for ${themeId}:`, err)
      )
    }
  }, [themeId, shouldDynamicallyLoadCSS])

  const contextValue = useMemo<ThemeContextValue>(() => {
    const isWhitelabel = themeId !== 'avios'
    return {
      themeId,
      isAviosTheme: !isWhitelabel,
      isWhitelabel,
    }
  }, [themeId])

  return (
    <ThemeContext.Provider value={contextValue}>
      {/* TODO: remove ChakraProvider after converting all components */}
      <CacheProvider value={styleCache}>
        <ChakraProvider value={getChakraThemeFromKey(contextValue.themeId)}>
          {children}
        </ChakraProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  )
}

export const usePlatformTheme = () => useContext(ThemeContext)
